import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { AuditLogEntry } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { membersQueryOptions } from '@/features/team';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { session } from '@/shared/session/session';

import { useActivity } from '../hooks/use-activity';
import { activityParams, ENTITY_TYPES, type ActivitySearch } from '../schemas/activity-search';
import { entityLink } from '../schemas/entity-link';

const ROUTE_ID = '/_authed/_active/org/activity' as const;
const ANY = '__any__';

function Actor({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation('activity');
  if (entry.actor.type === 'staff' && entry.actor.name) return <>{entry.actor.name}</>;
  return <span className="text-muted-foreground">{t(`actorType.${entry.actor.type}`)}</span>;
}

function Entity({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation('activity');
  const link = entityLink(entry);
  const label = t(`entityType.${entry.entityType}`, { defaultValue: entry.entityType });
  return link ? (
    <Link {...link} className="underline-offset-4 hover:underline">
      {label}
    </Link>
  ) : (
    <>{label}</>
  );
}

function Activity() {
  const { t } = useTranslation('activity');
  const format = useFormat();
  const { me } = session.useSession();
  const { can } = usePermissions();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/org/activity' });
  const debouncedAction = useDebouncedValue(search.action ?? '', 300);
  const query = useActivity(activityParams({ ...search, action: debouncedAction || undefined }));
  const members = useQuery({ ...membersQueryOptions(), enabled: can('staff.read') });
  const stores = me?.stores ?? [];
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const set = (patch: Partial<ActivitySearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const select = (
    id: string,
    label: string,
    value: string | undefined,
    options: Array<{ value: string; label: string }>,
    onChange: (value: string | undefined) => void,
  ) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value ?? ANY}
        onValueChange={(next) => onChange(next === ANY ? undefined : next)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{t('filters.any')}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const filtered = Object.values(search).some(Boolean);

  return (
    <PageContent>
      <PageHeader title={t('title')} description={t('description')} />
      <section
        aria-label={t('filters.label')}
        className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6"
        data-tour="org.activity.filters"
      >
        {select(
          'activity-actor',
          t('filters.actor'),
          search.actorId,
          (members.data ?? []).map((m) => ({
            value: m.user.id,
            label: m.user.name ?? m.user.email,
          })),
          (actorId) => set({ actorId }),
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="activity-action">{t('filters.action')}</Label>
          <Input
            id="activity-action"
            placeholder="order.cancel"
            className="font-mono text-[13px]"
            value={search.action ?? ''}
            onChange={(event) => set({ action: event.target.value || undefined })}
          />
        </div>
        {select(
          'activity-entity',
          t('filters.entity'),
          search.entityType,
          ENTITY_TYPES.map((type) => ({ value: type, label: t(`entityType.${type}`) })),
          (entityType) => set({ entityType }),
        )}
        {select(
          'activity-store',
          t('filters.store'),
          search.storeId,
          stores.map((s) => ({ value: s.id, label: s.name })),
          (storeId) => set({ storeId }),
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="activity-from">{t('filters.from')}</Label>
          <DatePicker
            id="activity-from"
            value={search.from ?? ''}
            timeZone="UTC"
            onChange={(day) => set({ from: day || undefined })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="activity-to">{t('filters.to')}</Label>
          <DatePicker
            id="activity-to"
            value={search.to ?? ''}
            timeZone="UTC"
            onChange={(day) => set({ to: day || undefined })}
          />
        </div>
      </section>
      {filtered ? (
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2"
          onClick={() =>
            void navigate({
              search: {
                actorId: undefined,
                action: undefined,
                entityType: undefined,
                entityId: undefined,
                storeId: undefined,
                from: undefined,
                to: undefined,
              },
              replace: true,
            })
          }
        >
          {t('filters.clear')}
        </Button>
      ) : null}
      {query.isPending ? (
        <Skeleton className="h-64" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={History}
          title={filtered ? t('empty.filteredTitle') : t('empty.title')}
          description={filtered ? t('empty.filtered') : t('empty.description')}
          tour="org.activity.empty"
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border" data-tour="org.activity.table">
            <Table aria-label={t('title')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">{t('columns.when')}</TableHead>
                  <TableHead>{t('columns.actor')}</TableHead>
                  <TableHead>{t('columns.action')}</TableHead>
                  <TableHead>{t('columns.entity')}</TableHead>
                  <TableHead className="pr-4">{t('columns.store')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((entry) => (
                  <TableRow key={entry.id} data-row-id={entry.id}>
                    <TableCell
                      className="pl-4 whitespace-nowrap"
                      title={format.utc(entry.createdAt)}
                    >
                      {format.dateTime(entry.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Actor entry={entry} />
                    </TableCell>
                    <TableCell className="font-mono text-[12px]">{entry.action}</TableCell>
                    <TableCell>
                      <Entity entry={entry} />
                    </TableCell>
                    <TableCell className="text-muted-foreground pr-4">
                      {entry.storeId
                        ? (stores.find((s) => s.id === entry.storeId)?.name ?? t('unknownStore'))
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {query.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                data-tour="org.activity.load-more"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? t('loadingMore') : t('loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </PageContent>
  );
}

/** Who changed what across the organisation; needs audit.read. */
export function ActivityPage() {
  return (
    <FeatureErrorBoundary name="activity">
      <Activity />
    </FeatureErrorBoundary>
  );
}
